import { type MutationFunction, useApolloClient, useLazyQuery, useMutation } from '@apollo/client';
import { formatThirdPartyFeesConfig } from '@openx/components-deal/lib/DealForm/utils/formatThirdPartyFeesConfig.ts';
import { useDealContext } from '@openx/components-deal/lib/utils/context/DealContext.tsx';
import {
  formStateToPackage,
  packageToFormState,
} from '@openx/components-package/components/PackageForm/utils/formStateConverter.ts';
import { type Deal, type DealFormDataWithTargeting, pmpDealType } from '@openx/types/deal.ts';
import type { PostForecastDataResponse } from '@openx/types/dealForecasting.ts';
import type { ExchangeDealForecastingDealParams, PackageCreateParams } from '@openx/types/gqlTypes.ts';
import type { PackageFormState } from '@openx/types/package.ts';
import type { DealForecastContextualEstimate } from '@openx/types/store/deal.ts';
import { contextualSegmentApiKey } from '@openx/types/targeting/contextualSegments.ts';
import type { TargetingState } from '@openx/types/targeting/targeting.ts';
import { toUTCDateTime } from '@openx/utils/lib/date.ts';
import { dealStateToApi } from '@openx/utils/state/deals/dealForm/utils.ts';
import pick from 'lodash/pick';
import { useFormContext } from 'react-hook-form';
import {
  CreateDealForecastingJobDocument,
  type CreateDealForecastingJobMutation,
  type CreateDealForecastingJobMutationVariables,
} from '../../../graphql/mutation/deals/CreateDealForecastJobId.generated.tsx';
import { GetAudienceCategoriesReachDocument } from '../../../graphql/query/audiences/GetAudienceCategoriesReach.generated.tsx';
import { ContextualEstimateDocument } from '../../../graphql/query/deals/GetContextualEstimate.generated.tsx';
import { GetPackageDocument } from '../../../graphql/query/packages/GetPackage.generated.tsx';

import { hasContextualSegments } from './hasContextualSegments.ts';
import { toEstimateNumber } from './toEstimateNumber.ts';

type UsePostForecastData = (DV360_DEMAND_PARTNER_ID: string) => {
  postForecast: MutationFunction<CreateDealForecastingJobMutation, CreateDealForecastingJobMutationVariables>;
  isLoading: boolean;
};

export const useGetForecastJobId: UsePostForecastData = (DV360_DEMAND_PARTNER_ID: string) => {
  const { getValues } = useFormContext<DealFormDataWithTargeting>();
  const {
    targeting: { targeting },
    ...deal
  } = getValues();
  const { useDealForecast } = useDealContext();
  const {
    actions: { setForecastJobID, setForecastLoading, setForecastingError },
  } = useDealForecast();

  const [getAudienceReach, { loading: loadingAudience }] = useLazyQuery(GetAudienceCategoriesReachDocument);
  const [getContextualEstimate, { loading: loadingContextual }] = useLazyQuery(ContextualEstimateDocument);

  const apolloClient = useApolloClient();

  // prepare deal for requesting job id
  const {
    deal_id,
    start_date,
    end_date,
    open_auction_access,
    third_party_fees_config,
    third_party_fees_config_enabled,
    ...dealRest
  } = deal;

  const shouldOpenAuctionAccessBeSend =
    deal.pmp_deal_type !== pmpDealType.Guaranteed && deal.pmp_deal_type !== pmpDealType.WithinOpenAuction;

  const dealData = {
    ...dealRest,
    deal_id: deal_id?.trim(),
    end_date: end_date ? toUTCDateTime(end_date) : null,
    start_date: toUTCDateTime(start_date ?? ''),
    third_party_fees_config: formatThirdPartyFeesConfig(third_party_fees_config_enabled, third_party_fees_config),
    ...(shouldOpenAuctionAccessBeSend && { open_auction_access }),
  } as Deal;

  const { apiDeal } = dealStateToApi(dealData, dealData.uid, {
    dv360DemandPartnerId: DV360_DEMAND_PARTNER_ID,
  });

  const dealApiData = pick(apiDeal, [
    'name',
    'deal_id',
    'start_date',
    'end_date',
    'currency',
    'deal_price',
    'pmp_deal_type',
    'deal_participants',
    'third_party_fees_config',
    'enable_dv360_inventory_package',
  ]);

  dealApiData?.deal_participants?.forEach(participant => {
    //@ts-expect-error
    delete participant.main_buyer_id;
  });

  let targetingDataForm: TargetingState | undefined = targeting;

  if (deal.package_uid) {
    const packageFromCache = apolloClient.readQuery({
      query: GetPackageDocument,
      variables: { id: deal.package_uid },
    });

    const pckg = packageFromCache?.packageById?.obj ?? undefined;

    // extend api response with targeting from form data
    targetingDataForm = pckg ? { ...packageToFormState(pckg)?.targeting, ...targetingDataForm } : undefined;
  }

  // added private_market to formStateToPackage so function won't break
  const packageConverted = formStateToPackage({
    private_market: {},
    targeting: targetingDataForm ?? {},
  } as PackageFormState);

  const packageData = {
    account_uid: deal.account_uid || '',
    targeting: {
      ...(packageConverted.targeting || {}),
    },
    url_targeting: packageConverted?.url_targeting || {},
  };

  // create a request to get forecast job id
  const [postForecast, { loading }] = useMutation(CreateDealForecastingJobDocument, {
    onCompleted: async data => {
      const audienceIDFromForm = (getValues('targeting.targeting.audience.openaudience_custom.val') as string) || '';
      const audienceIDFromSelectedPckg = packageData.targeting.audience?.openaudience_custom?.val;
      const openAudienceId = audienceIDFromForm || audienceIDFromSelectedPckg || '';

      let notIncludedFields = data.createDealForecastingJob?.not_included_fields ?? [];
      let audienceMonthlyRequests: number | null = null;
      let contextualEstimate: DealForecastContextualEstimate | null = null;

      const audienceReachPromise = openAudienceId
        ? getAudienceReach({ variables: { id: openAudienceId.replace('openaudience-', '') } })
        : Promise.resolve(null);

      const contextualEstimatePromise = hasContextualSegments(targetingDataForm)
        ? getContextualEstimate({ variables: { targeting: packageData.targeting } })
        : Promise.resolve(null);

      const [audienceReachResponse, contextualEstimateResponse] = await Promise.all([
        audienceReachPromise,
        contextualEstimatePromise,
      ]);

      if (audienceReachResponse && !audienceReachResponse.error) {
        audienceMonthlyRequests = audienceReachResponse.data?.audience_categories_reach_all?.[0]?.total || null;
        notIncludedFields = notIncludedFields.filter(field => field !== 'targeting.audience.openaudience_custom');
      }

      if (
        contextualEstimateResponse &&
        !contextualEstimateResponse.error &&
        contextualEstimateResponse.data?.contextualEstimate
      ) {
        const { impressions, marketRequests } = contextualEstimateResponse.data.contextualEstimate;
        contextualEstimate = {
          impressions: toEstimateNumber(impressions.estimate),
          market_requests: toEstimateNumber(marketRequests.estimate),
        };
        // The forecaster reports the inner keys of `contextual_segment` (`.includes`, `.excludes`), not the field itself.
        const contextualSegmentField = `targeting.${contextualSegmentApiKey}`;
        notIncludedFields = notIncludedFields.filter(
          field => field !== contextualSegmentField && !field.startsWith(`${contextualSegmentField}.`)
        );
      }

      setForecastJobID(
        {
          ...data.createDealForecastingJob,
          not_included_fields: notIncludedFields,
        } as PostForecastDataResponse,
        audienceMonthlyRequests,
        contextualEstimate
      );
    },
    onError: error => {
      setForecastingError(error);
    },
  });

  const postForecastRequest = () => {
    setForecastLoading();

    return postForecast({
      variables: {
        deal: dealApiData as ExchangeDealForecastingDealParams,
        package: packageData as unknown as PackageCreateParams,
      },
    });
  };

  return {
    isLoading: loading || loadingAudience || loadingContextual,
    postForecast: postForecastRequest,
  };
};
